import type {
  SimulationAuthorization,
  SimulationLimits,
  SimulationMode
} from '@morpho-org/evm-simulation'

import { type Address, ChainId } from '@morpho-org/blue-sdk'
import { parseJsonResponse } from '@repo/utils'
import { type Hex, isAddress } from 'viem'
import { z } from 'zod'

import { getDeploymentHeaders } from '@/config/deployment'

/**
 * Local Next.js API route that simulates a transaction bundle via
 * `@morpho-org/evm-simulation`.
 */
export const SIMULATE_API_URL = '/api/simulate'

/**
 * Client-side fetch ceiling. Higher than the SDK's 8s server-side budget so
 * network overhead is absorbed before we surface a `simulation_api_call` error.
 */
const FETCH_TIMEOUT_MS = 12_000

/**
 * Monad's `eth_simulateV1` rejects a sender with contract code (Safe, smart wallet) with "sender not
 * eoa"; EIP-7702 delegated EOAs pass. Skip simulating those senders there rather than fake an EOA,
 * and treat a sender whose bytecode isn't known yet as one of them.
 */
export const isSimulationUnsupportedSender = (
  chainId: number,
  account: { isContract: boolean; isDelegatedEoa: boolean; isByteCodeResolved: boolean }
): boolean =>
  chainId === ChainId.MonadMainnet &&
  (!account.isByteCodeResolved || (account.isContract && !account.isDelegatedEoa))

/** Must match the requirement-state wait budget in the /api/simulate route. */
const SIMULATION_HEAD_WAIT_TIMEOUT_MS = 10_000

type SimulationTracking = { sdkAction?: string; sdkVersion?: string; reviewSessionId?: string }

const getSimulationTrackingBody = (tracking?: SimulationTracking) => ({
  sdkAction: tracking?.sdkAction,
  sdkVersion: tracking?.sdkVersion,
  reviewSessionId: tracking?.reviewSessionId
})

const hexSchema = z.custom<Hex>(
  (v): v is Hex => typeof v === 'string' && /^0x[0-9a-fA-F]*$/.test(v),
  { message: 'data must be 0x-prefixed hex' }
)

/** One per-call top-level `returnData` (Option B); `status` is `true` on a successful bundle today. */
const simulatedCallSchema = z.object({
  returnData: hexSchema,
  status: z.boolean().optional()
})

const simulationResponseSchema = z.object({
  // Loose: success carries `AccountAssetChanges[]`, BLACKLIST_ERROR carries `RetainedAsset[]`; parse via `parseSimulatedAssetChanges`.
  assetChanges: z.any().optional(),
  // Per-call `returnData`, 1:1 with the simulated bundle (Option B post-action reads). Absent on
  // error responses; the market-actions-v2 flow decodes the trailing entries, every other caller ignores it.
  calls: z.array(simulatedCallSchema).optional(),
  success: z.boolean(),
  code: z.string().optional(),
  error: z.string().optional(),
  reason: z.string().optional()
})

/** A relayed per-call simulation result (`returnData` for Option B post-action decoding). */
export type SimulatedCall = z.infer<typeof simulatedCallSchema>

const addressSchema = z.custom<Address>(
  (v): v is Address => typeof v === 'string' && isAddress(v),
  { message: 'invalid address' }
)

/** Per-token change; `diff` is the signed net delta (raw units), parsed from the wire string to `bigint`. */
const assetChangeSchema = z.object({
  token: addressSchema,
  symbol: z.string().optional(),
  decimals: z.number().optional(),
  diff: z.string().transform(value => BigInt(value))
})

/** Net per-token balance changes for one account over the whole bundle. */
const accountAssetChangesSchema = z.object({
  account: addressSchema,
  changes: z.array(assetChangeSchema)
})

export type AccountAssetChanges = z.infer<typeof accountAssetChangesSchema>

/** Parses the success-path `assetChanges` (wire `diff` → `bigint`); `undefined` if the shape doesn't match. */
export const parseSimulatedAssetChanges = (raw: unknown): AccountAssetChanges[] | undefined => {
  const parsed = z.array(accountAssetChangesSchema).safeParse(raw)
  return parsed.success ? parsed.data : undefined
}

export interface Transaction {
  from: Address
  to: Address
  value: string
  data: Hex
}

/** Maps a built tx (`bigint` value) to the simulate-route wire shape. */
export const toSimulationTx = (
  from: Address,
  tx: { to: Address; value: bigint; data: Hex }
): Transaction => ({ from, to: tx.to, value: tx.value.toString(), data: tx.data })

// Walks the value itself: a `JSON.stringify` replacer would only see the strings the app-wide
// `BigInt.prototype.toJSON` (if anything has patched it) already produced.
const tagBigInts = (value: unknown): unknown => {
  if (typeof value === 'bigint') return { __bigint: value.toString() }
  if (Array.isArray(value)) return value.map(tagBigInts)
  if (typeof value !== 'object' || value === null) return value
  return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, tagBigInts(entry)]))
}

/**
 * JSON-safe authorization payload: bigints travel as `{ __bigint: "<decimal>" }` tags the route
 * revives before handing them to the SDK, which validates every field.
 */
export const serializeSimulationAuthorizations = (
  authorizations: readonly SimulationAuthorization[]
): unknown[] => authorizations.map(tagBigInts)

/** JSON-safe slippage limits, tagged like the authorizations. */
export const serializeSimulationLimits = (limits: SimulationLimits): unknown => tagBigInts(limits)

/**
 * Query-key identity for preview authorizations. Builders stamp a fresh signature `deadline` on
 * every rebuild, so it is left out; the request still sends it.
 */
export const simulationAuthorizationsKey = (
  authorizations: readonly SimulationAuthorization[] | undefined
): string =>
  JSON.stringify(authorizations ?? [], (key, value: unknown) => {
    if (key === 'deadline') return undefined
    return typeof value === 'bigint' ? value.toString() : value
  })

/** Codes that must block the flow: a retained-asset violation or a simulation result the SDK could not verify. */
const NON_BYPASSABLE_SIMULATION_CODES: ReadonlySet<string> = new Set([
  'BLACKLIST_ERROR',
  'INVALID_SIMULATION_RESPONSE',
  'MISSING_VERIFICATION_EVIDENCE',
  'CONSUMER_LIMIT_VIOLATION'
])

export type SimulationFailureType = 'simulation' | 'simulation_api_call'

export const SIMULATION_REVERT_CATEGORIES = [
  'insufficient_balance',
  'insufficient_allowance',
  'authorization_already_set',
  'invalid_signature',
  'signature_expired',
  'insufficient_liquidity',
  'insufficient_collateral',
  'stale_nonce',
  'unknown'
] as const

export type SimulationRevertCategory = (typeof SIMULATION_REVERT_CATEGORIES)[number]

/** Reverts a user can act on themselves; everything else is treated as a potential bug. */
export const EXPECTED_SIMULATION_REVERT_CATEGORIES: ReadonlySet<SimulationRevertCategory> = new Set(
  SIMULATION_REVERT_CATEGORIES.filter(c => c !== 'unknown')
)

/** User-facing copy per revert category (≤75 chars, see docs/COPY.md); surfaced in the toast via `formatErrorMessage`. */
export const SIMULATION_REVERT_MESSAGES: Record<SimulationRevertCategory, string> = {
  insufficient_balance: 'Insufficient balance. Add funds, then try again.',
  insufficient_allowance: 'Token approval too low. Close this dialog and retry the transaction.',
  authorization_already_set: 'Authorization already in the requested state. Refresh and try again.',
  invalid_signature: 'Signature could not be verified. Try again to continue.',
  signature_expired: 'Signature expired. Close this dialog and retry the transaction.',
  insufficient_liquidity: 'Not enough liquidity. Close this dialog and try a smaller amount.',
  insufficient_collateral: 'Insufficient collateral. Close this dialog and adjust the amount.',
  stale_nonce: 'Signature no longer valid. Close this dialog and retry the transaction.',
  unknown: 'Simulation detected an issue. Please try again.'
}

/** Shown when v5 `limits` reject the bundle: amounts moved past the slippage tolerance (≤75 chars). */
export const SIMULATION_LIMIT_VIOLATION_MESSAGE =
  'Amounts moved past the slippage limit. Close this dialog and start over.'

/** Codes where the SDK couldn't verify the simulated outcome; the bundle itself may be fine. */
export const UNVERIFIED_SIMULATION_CODES: ReadonlySet<string> = new Set([
  'INVALID_SIMULATION_RESPONSE',
  'MISSING_VERIFICATION_EVIDENCE'
])

export const SIMULATION_UNVERIFIED_MESSAGE =
  "The transaction outcome couldn't be verified. Try again."

export function classifySimulationRevert(reason: string | undefined): SimulationRevertCategory {
  const normalized = reason?.toLowerCase() ?? ''
  if (
    normalized.includes('transfer amount exceeds allowance') ||
    normalized.includes('insufficient allowance')
  ) {
    return 'insufficient_allowance'
  }
  if (
    normalized.includes('transfer amount exceeds balance') ||
    normalized.includes('insufficient balance')
  ) {
    return 'insufficient_balance'
  }
  // Morpho Blue ErrorsLib.ALREADY_SET is shared by several setters; only setAuthorization reaches user flows today.
  if (normalized.includes('already set')) return 'authorization_already_set'
  // A stale signature nonce can't be fixed by re-signing — the whole flow must restart.
  if (normalized.includes('invalid nonce')) return 'stale_nonce'
  // An expired signature can't be fixed by re-signing — the deadline is captured at build time.
  if (normalized.includes('signature expired')) return 'signature_expired'
  if (normalized.includes('invalid signature')) return 'invalid_signature'
  if (normalized.includes('insufficient liquidity')) return 'insufficient_liquidity'
  if (normalized.includes('insufficient collateral')) return 'insufficient_collateral'
  return 'unknown'
}

export interface SimulationErrorOptions {
  failureType: SimulationFailureType
  chainId: number
  transactions: Transaction[]
  details?: {
    code?: string
    reason?: string
    error?: string
    httpStatus?: number
    assetChanges?: unknown
  }
  /** Set by `checkSimulation` for reverts only; other simulation failures leave it undefined. */
  revertCategory?: SimulationRevertCategory
}

/**
 * Custom error class for simulation failures.
 * Contains detailed simulation context for logging.
 */
export class SimulationError extends Error {
  public readonly failureType: SimulationFailureType
  public readonly chainId: number
  public readonly transactions: Transaction[]
  public readonly details?: SimulationErrorOptions['details']
  /** `undefined` for `simulation_api_call` failures — not a revert, so it must not pollute the `unknown` bucket. */
  public readonly revertCategory: SimulationRevertCategory | undefined

  constructor(message: string, options: SimulationErrorOptions) {
    super(message)
    this.name = 'SimulationError'
    this.failureType = options.failureType
    this.chainId = options.chainId
    this.transactions = options.transactions
    this.details = options.details
    this.revertCategory = options.failureType === 'simulation' ? options.revertCategory : undefined
  }

  /**
   * Checks if the simulation error is acceptable and can be bypassed by the user.
   * Reverts and backend outages are acceptable; a blacklist violation or a result the SDK could
   * not verify (malformed response, reorged pinned block, missing evidence) is not.
   */
  get isAcceptable(): boolean {
    const code = this.details?.code
    return code === undefined || !NON_BYPASSABLE_SIMULATION_CODES.has(code)
  }

  /**
   * Whether the revert matches a user-actionable cause (low balance, already-set
   * authorization, expired signature, …) rather than a potential bug.
   */
  get isExpectedRevert(): boolean {
    return (
      this.revertCategory !== undefined &&
      EXPECTED_SIMULATION_REVERT_CATEGORIES.has(this.revertCategory)
    )
  }
}

/**
 * Final-mode check of the signed bundle via the local /api/simulate route, against real on-chain
 * permissions (no simulated authorizations).
 * @param chainId - Chain ID for the simulation
 * @param transactions - Transactions to simulate
 * @param options.minBlockNumber - Latest mined requirement block the simulated state must include
 * @param options.requirementTxHashes - Mined approval/authorization txs; the route waits until the
 *   simulation backend serves their receipts, then pins the simulation to a head that includes them
 * @param options.limits - v5 slippage checks on the final action (`buildSimulationLimits`)
 * @throws SimulationError if simulation detects issues or transaction would fail
 */
export const checkSimulation = async (
  chainId: ChainId,
  transactions: Transaction[],
  options?: {
    minBlockNumber?: bigint
    requirementTxHashes?: readonly Hex[]
    limits?: SimulationLimits
    tracking?: SimulationTracking
  }
) => {
  const simulationData = await getSimulate({
    chainId,
    transactions,
    mode: 'final',
    limits: options?.limits,
    minBlockNumber: options?.minBlockNumber,
    requirementTxHashes: options?.requirementTxHashes,
    tracking: options?.tracking
  })
  if (!simulationData.success) {
    throw toSimulationError(simulationData, chainId, transactions)
  }
}

const getClientErrorCode = (error: unknown) => {
  if (!(error instanceof Error)) return undefined
  if (error.name === 'TimeoutError') return 'CLIENT_TIMEOUT'
  if (error.name === 'AbortError') return 'CLIENT_ABORTED'
  return undefined
}

/**
 * Fetches simulation results from the local /api/simulate route.
 * @param params - Simulation parameters
 * @param params.chainId - Target chain ID
 * @param params.transactions - Transactions to simulate
 * @param params.mode - `preview` for unsigned bundles (with `authorizations`), `final` for signed ones
 * @param params.authorizations - Preview-only pending approvals/authorizations the SDK prepares
 * @returns Validated simulation response with success status and asset changes
 * @throws SimulationError if simulation API call fails
 */
export const getSimulate = async ({
  chainId,
  transactions,
  mode,
  authorizations,
  limits,
  minBlockNumber,
  requirementTxHashes,
  tracking
}: {
  chainId: number
  transactions: Transaction[]
  mode: SimulationMode
  authorizations?: readonly SimulationAuthorization[]
  /** v5 slippage checks on the final action; omitted when it has nothing to quote. */
  limits?: SimulationLimits
  minBlockNumber?: bigint
  requirementTxHashes?: readonly Hex[]
  tracking?: SimulationTracking
}) => {
  let httpStatus: number | undefined
  const waitsForRequirements = minBlockNumber !== undefined || !!requirementTxHashes?.length
  try {
    const simulation = await fetch(SIMULATE_API_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...getDeploymentHeaders() },
      body: JSON.stringify({
        chainId,
        transactions,
        mode,
        ...(authorizations?.length && {
          authorizations: serializeSimulationAuthorizations(authorizations)
        }),
        ...(limits?.operations?.length && { limits: serializeSimulationLimits(limits) }),
        ...(minBlockNumber !== undefined && { minBlockNumber: minBlockNumber.toString() }),
        ...(requirementTxHashes?.length && { requirementTxHashes }),
        ...getSimulationTrackingBody(tracking)
      }),
      signal: AbortSignal.timeout(
        FETCH_TIMEOUT_MS + (waitsForRequirements ? SIMULATION_HEAD_WAIT_TIMEOUT_MS : 0)
      )
    })
    httpStatus = simulation.status
    const { data: simulationData, error: parseError } = await parseJsonResponse<{
      code?: unknown
      error?: unknown
    }>(simulation, { url: SIMULATE_API_URL })
    if (parseError) {
      throw new SimulationError("Transaction couldn't be simulated. Please try again.", {
        failureType: 'simulation_api_call',
        chainId,
        transactions,
        details: {
          code: getClientErrorCode(parseError.cause) ?? 'INVALID_RESPONSE',
          error: parseError.message,
          httpStatus
        }
      })
    }

    // Non-2xx responses are API/backend failures (validation, external service
    // outage, unknown error), not EVM simulation results. Surface them as
    // `simulation_api_call` so they don't trip the `logger.fatal` path that's
    // reserved for real on-chain reverts.
    if (!simulation.ok) {
      throw new SimulationError("Transaction couldn't be simulated. Please try again.", {
        failureType: 'simulation_api_call',
        chainId,
        transactions,
        details: {
          code:
            typeof simulationData?.code === 'string' && simulationData.code !== ''
              ? simulationData.code
              : 'HTTP_ERROR',
          error: typeof simulationData?.error === 'string' ? simulationData.error : undefined,
          httpStatus
        }
      })
    }

    const validatedSimulation = simulationResponseSchema.safeParse(simulationData)
    if (!validatedSimulation.success) {
      throw new SimulationError("Transaction couldn't be simulated. Please try again.", {
        failureType: 'simulation_api_call',
        chainId,
        transactions,
        details: {
          code: 'INVALID_RESPONSE',
          error: validatedSimulation.error.message,
          httpStatus
        }
      })
    }

    return { ...validatedSimulation.data, httpStatus }
  } catch (error) {
    if (error instanceof SimulationError) throw error
    const errorMessage = error instanceof Error ? error.message : String(error)
    const code = getClientErrorCode(error) ?? 'NETWORK_ERROR'
    throw new SimulationError("Transaction couldn't be simulated. Please try again.", {
      failureType: 'simulation_api_call',
      chainId,
      transactions,
      details: { code, error: errorMessage, httpStatus }
    })
  }
}

export function toSimulationError(
  result: Awaited<ReturnType<typeof getSimulate>>,
  chainId: number,
  transactions: Transaction[]
) {
  const revertCategory =
    result.code === 'SIMULATION_REVERTED' ? classifySimulationRevert(result.reason) : undefined
  return new SimulationError(simulationErrorMessage(result.code, revertCategory), {
    failureType: 'simulation',
    chainId,
    transactions,
    details: {
      code: result.code,
      reason: result.reason,
      error: result.error,
      httpStatus: result.httpStatus,
      assetChanges: result.assetChanges
    },
    revertCategory
  })
}

function simulationErrorMessage(
  code: string | undefined,
  revertCategory: SimulationRevertCategory | undefined
) {
  if (code === 'CONSUMER_LIMIT_VIOLATION') return SIMULATION_LIMIT_VIOLATION_MESSAGE
  if (UNVERIFIED_SIMULATION_CODES.has(code ?? '')) return SIMULATION_UNVERIFIED_MESSAGE
  if (revertCategory) return SIMULATION_REVERT_MESSAGES[revertCategory]
  return SIMULATION_REVERT_MESSAGES.unknown
}
